import React from "react";
import { InputAdornment, TextField } from "@mui/material";
import { Check, ErrorOutline } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/vi";
import { commonFieldSx } from "./dropdownStyles";

const statusIcon = {
  success: <Check fontSize="small" sx={{ color: "#20a354" }} />,
  error: <ErrorOutline fontSize="small" sx={{ color: "#d32f2f" }} />,
};

export default function AppTextField({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  error = false,
  success = false,
  helperText,
  placeholder,
  type = "text",
  defaultToday = false,
  multiline = false,
  minRows,
  maxRows,
  fullWidth = true,
  size = "small",
  className,
  sx,
  name,
  inputProps,
  InputProps,
  startIcon,
  endIcon,
  showCount = false,
  maxLength,
  ...props
}) {
  const resolvedError = Boolean(error);
  const resolvedSuccess = success && !resolvedError;
  const countText = showCount && maxLength ? `${String(value ?? "").length} / ${maxLength}` : "";
  const resolvedHelperText = helperText || countText || undefined;
  const resolvedEndIcon = endIcon || (resolvedError ? statusIcon.error : resolvedSuccess ? statusIcon.success : null);
  const valueProps = value === undefined ? {} : { value: value ?? "" };
  // const dateValue = value ? dayjs(value) : null;
  const dateValue = value
    ? dayjs(value)
    : defaultToday
      ? dayjs()
      : null;

  const sizeFieldSx = size === "medium" ? {
    "& .MuiOutlinedInput-root": {
      minHeight: 50,
      borderRadius: "8px",
      backgroundColor: "#fff",
      transition: "box-shadow .16s ease, border-color .16s ease",
      "& fieldset": {
        borderColor: "#c7cdd4",
      },
      "&:hover fieldset": {
        borderColor: "#7aaee0",
      },
      // "&.Mui-focused": {
      //   boxShadow: "0 0 0 3px rgba(11, 92, 171, .13)",
      // },
      "&.Mui-focused fieldset": {
        borderColor: "#0b5cab",
        borderWidth: "1.6px",
      },
      "&.Mui-error.Mui-focused": {
        boxShadow: "0 0 0 3px rgba(211, 47, 47, .12)",
      },
      "&.Mui-disabled": {
        backgroundColor: "#f8fafc",
      },
    },
    "& .MuiInputLabel-root": {
      transform: "translate(14px, 15px) scale(1)",
      color: "#526171",
      fontSize: 14,
      lineHeight: 1.2,
      "&.MuiInputLabel-shrink": {
        transform: "translate(12px, -7px) scale(.78)",
        backgroundColor: "#fff",
        padding: "0 5px",
      },
      "&.Mui-focused": {
        color: "#0b5cab",
      },
    },
    "& .MuiInputBase-input": {
      fontSize: 14,
      lineHeight: "22px",
    },
  } : {};

  if (type === "date") {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="vi">
        <DatePicker
          label={label}
          value={dateValue?.isValid() ? dateValue : null}
          onChange={(nextValue) => {
            onChange?.(nextValue?.isValid?.() ? nextValue.format("YYYY-MM-DD") : "", nextValue);
          }}
          format="DD/MM/YYYY"
          disabled={disabled}
          dayOfWeekFormatter={(day) => day.format("dd")}
          slotProps={{
            textField: {
              ...props,
              name,
              className,
              required,
              error: resolvedError,
              helperText: resolvedHelperText,
              placeholder: placeholder || "dd/MM/yyyy",
              fullWidth,
              size,
              inputProps: {
                maxLength,
                ...inputProps,
                ...props.inputProps,
              },
              FormHelperTextProps: {
                sx: countText && !helperText ? { textAlign: "right", color: "#4f8edb" } : undefined,
                ...props.FormHelperTextProps,
              },
              sx: {
                ...commonFieldSx,
                ...sizeFieldSx,
                ...(resolvedSuccess ? {
                  "& .MuiOutlinedInput-root fieldset": { borderColor: "#78c785" },
                  "& .MuiInputLabel-root.MuiInputLabel-shrink": { color: "#2f9c4f" },
                } : {}),
                ...sx,
              },
            },
          }}
        />
      </LocalizationProvider>
    );
  }

  return (
    <TextField
      {...props}
      name={name}
      className={className}
      label={label}
      {...valueProps}
      onChange={(event) => onChange?.(event.target.value, event)}
      required={required}
      disabled={disabled}
      error={resolvedError}
      helperText={resolvedHelperText}
      placeholder={placeholder}
      type={type}
      multiline={multiline}
      minRows={multiline ? minRows || 3 : minRows}
      maxRows={maxRows}
      fullWidth={fullWidth}
      size={size}
      sx={{
        ...commonFieldSx,
        ...sizeFieldSx,
        ...(resolvedSuccess ? {
          "& .MuiOutlinedInput-root fieldset": { borderColor: "#78c785" },
          "& .MuiInputLabel-root.MuiInputLabel-shrink": { color: "#2f9c4f" },
        } : {}),
        ...sx,
      }}
      inputProps={{
        maxLength,
        ...inputProps,
      }}
      InputLabelProps={type === "date" ? { shrink: true } : props.InputLabelProps}
      InputProps={{
        ...InputProps,
        startAdornment: startIcon ? (
          <InputAdornment position="start">{startIcon}</InputAdornment>
        ) : InputProps?.startAdornment,
        endAdornment: resolvedEndIcon ? (
          <InputAdornment position="end">{resolvedEndIcon}</InputAdornment>
        ) : InputProps?.endAdornment,
      }}
      FormHelperTextProps={{
        sx: countText && !helperText ? { textAlign: "right", color: "#4f8edb" } : undefined,
        ...props.FormHelperTextProps,
      }}
    />
  );
}
